import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box } from '@ovComponents/1-primative/box/box';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { Menu } from '@ovComponents/2-component/menu/menu';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents as analyticsEvents } from '@utils/analytics/analytics';
import { useAnalyticsContext } from '@utils/analytics/contextDetection';
import { useEditAnalytics } from '@utils/analytics/editAnalytics';
import {
  calculateDurationMilliseconds,
  countFilledFields,
  summarizeInteractedFields,
} from '@utils/analytics/eventMetrics';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal } from '../confirmationDialog/confirmationDialog';
import { CopyId } from '../copyId/copyId';

export const FormModal = ({
  onSubmit,
  loading,
  children,
  title,
  titleAdornment,
  open,
  handleClose,
  formButton,
  maxWidth = 'sm',
  disabled = false,
  state,
  initialState,
  menuItems,
  testId,
  copyId = undefined,
  formType = 'generic-form',
  context,
  isEdit = false,
}: {
  onSubmit: (event: any) => void;
  loading: boolean;
  children: any | any[];
  title: string;
  titleAdornment?: React.ReactNode;
  open: boolean;
  handleClose: () => void;
  formButton: string;
  maxWidth?: any;
  disabled?: boolean;
  state?: any;
  initialState?: any;
  menuItems?: any[];
  testId?: string;
  copyId?: string;
  formType?: string;
  context?: string;
  isEdit?: boolean;
}) => {
  const [exitConfirmationOpen, setExitConfirmationOpen] = useState(false);
  const { t } = useTranslation('shared');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { getFormType } = useAnalyticsContext();
  const { trackEditComplete } = useEditAnalytics();
  const formStartTime = useRef<Date | null>(null);
  const [filledFieldsCount, setFilledFieldsCount] = useState(0);
  const [fieldInteractionCount, setFieldInteractionCount] = useState(0);
  const previousStateRef = useRef<any>(null);
  const isFirstRender = useRef(true);
  const interactedFields = useRef<Set<string>>(new Set());

  // Automatically detect form type based on current page
  const detectedFormType = getFormType(formType);
  const [contextMenuAnchorEl, setContextMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const contextMenuOpen = Boolean(contextMenuAnchorEl);
  const openContextMenu = (onElement: HTMLElement) => {
    setContextMenuAnchorEl(onElement);
  };
  const closeContextMenu = () => setContextMenuAnchorEl(null);

  // Track field changes by detecting which fields have been interacted with
  const trackFieldInteraction = (currentState: any, previousState: any) => {
    if (!currentState || !previousState) {
      return;
    }

    const findChangedFields = (current: any, previous: any) => {
      // Use a Set to get all unique keys from both objects
      const allKeys = new Set([
        ...Object.keys(current),
        ...Object.keys(previous),
      ]);

      allKeys.forEach((key) => {
        const currentValue = JSON.stringify(current[key]);
        const previousValue = JSON.stringify(previous[key]);

        // If the stringified values are different, count it as an interaction.
        // This is simpler and correctly captures any change.
        if (currentValue !== previousValue) {
          interactedFields.current.add(key);
        }
      });
    };

    findChangedFields(currentState, previousState);
  };

  // Track form field interactions
  useEffect(() => {
    if (state && initialState) {
      const currentFilledCount = countFilledFields(state);
      setFilledFieldsCount(currentFilledCount);

      // Track field interactions by comparing with initial state
      // Clear and re-calculate interactions from scratch
      interactedFields.current.clear();
      trackFieldInteraction(state, initialState);
      setFieldInteractionCount(interactedFields.current.size);

      // Mark that we've had at least one render
      isFirstRender.current = false;
    }
  }, [state, initialState, formType, title, sendAnalytic]);

  // Track form start time when modal opens (only for create operations)
  useEffect(() => {
    if (open) {
      // Always reset all tracking variables when modal opens
      formStartTime.current = new Date();
      setFieldInteractionCount(0);
      previousStateRef.current = null;
      isFirstRender.current = true;
      interactedFields.current.clear();
      setFilledFieldsCount(0);

      // Only track modal open for create operations, not edit operations
      // Skip generic modalOpen tracking for fee-grid and billing-schedule as they have their own specific tracking
      if (
        !isEdit &&
        detectedFormType !== 'fee-grid' &&
        detectedFormType !== 'billing-schedule'
      ) {
        sendAnalytic(analyticsEvents.modalOpen(detectedFormType, context), {
          formTitle: title,
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        });
      }
    }
  }, [open, formType, context, title, sendAnalytic, isEdit]);

  const handleFormSubmit = async (event: any) => {
    const formDuration = calculateDurationMilliseconds(formStartTime.current);

    try {
      await onSubmit(event);

      // Track field interaction summary on successful completion
      // Recalculate field interactions to ensure accurate count
      interactedFields.current.clear();
      trackFieldInteraction(state, initialState);
      const {
        fieldInteractionCount: finalFieldInteractionCount,
        interactedFields: interactedFieldNames,
      } = summarizeInteractedFields(interactedFields.current);

      // Track specific fee grid summary events (skip generic events for fee grids)
      if (detectedFormType === 'fee-grid') {
        const summaryEvent = isEdit
          ? analyticsEvents.feeGridEditSummary
          : analyticsEvents.feeGridAddSummary;
        sendAnalytic(summaryEvent, {
          formTitle: title,
          fieldInteractionCount: finalFieldInteractionCount,
          interactedFields: interactedFieldNames,
          formDuration: formDuration / 1000,
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        });
      } else if (detectedFormType === 'billing-schedule') {
        // Track specific billing schedule summary events (skip generic events for billing schedules)
        const summaryEvent = isEdit
          ? analyticsEvents.billingScheduleEditSummary
          : analyticsEvents.billingScheduleAddSummary;
        sendAnalytic(summaryEvent, {
          formTitle: title,
          fieldInteractionCount: finalFieldInteractionCount,
          interactedFields: interactedFieldNames,
          formDuration: formDuration / 1000,
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        });
      } else if (
        detectedFormType === 'assetClasses' ||
        detectedFormType === 'asset-classes'
      ) {
        // Track specific asset classes summary events (skip generic events for asset classes)
        const summaryEvent = isEdit
          ? analyticsEvents.assetClassesEditSummary
          : analyticsEvents.assetClassesAddSummary;
        sendAnalytic(summaryEvent, {
          formTitle: title,
          fieldInteractionCount: finalFieldInteractionCount,
          interactedFields: interactedFieldNames,
          formDuration: formDuration / 1000,
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        });
      } else {
        // Track create event for new items (only for create operations, not edit)
        // Skip generic modalSubmit tracking for fee-grid, billing-schedule, and asset classes as they have their own specific tracking
        if (
          !isEdit &&
          detectedFormType !== 'fee-grid' &&
          detectedFormType !== 'billing-schedule' &&
          detectedFormType !== 'assetClasses' &&
          detectedFormType !== 'asset-classes'
        ) {
          sendAnalytic(analyticsEvents.modalSubmit(detectedFormType, context), {
            formTitle: title,
            filledFieldsCount,
            formDuration: formDuration / 1000,
            previousPage: document.referrer || 'direct',
            timestamp: new Date().toISOString(),
          });
        }

        // Track edit completion for edit operations
        if (isEdit) {
          trackEditComplete(detectedFormType, undefined, {
            formTitle: title,
            fieldInteractionCount,
            formDuration: formDuration / 1000,
          });
        }

        // Track generic field interaction summary for non-fee-grid forms
        sendAnalytic(
          analyticsEvents.formFieldInteraction(
            detectedFormType,
            'summary',
            isEdit ? 'edit_completion' : 'add_completion',
          ),
          {
            formTitle: title,
            fieldInteractionCount: finalFieldInteractionCount,
            interactedFields: interactedFieldNames,
            formDuration: formDuration / 1000,
            previousPage: document.referrer || 'direct',
            timestamp: new Date().toISOString(),
          },
        );
      }
    } catch (error) {
      // Track field interaction summary on error
      // Recalculate field interactions to ensure accurate count
      interactedFields.current.clear();
      trackFieldInteraction(state, initialState);
      const {
        fieldInteractionCount: finalFieldInteractionCount,
        interactedFields: interactedFieldNames,
      } = summarizeInteractedFields(interactedFields.current);

      sendAnalytic(
        analyticsEvents.formFieldInteraction(
          detectedFormType,
          'summary',
          isEdit ? 'edit_error' : 'add_error',
        ),
        {
          formTitle: title,
          fieldInteractionCount: finalFieldInteractionCount,
          interactedFields: interactedFieldNames,
          formDuration: formDuration / 1000,
          error: error instanceof Error ? error.message : 'Unknown error',
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        },
      );
      throw error;
    }
  };

  const handleFormClose = () => {
    const formDuration = calculateDurationMilliseconds(formStartTime.current);

    // Track field interaction summary on abandonment
    if (filledFieldsCount > 0) {
      // Recalculate field interactions to ensure accurate count
      interactedFields.current.clear();
      trackFieldInteraction(state, initialState);
      const {
        fieldInteractionCount: finalFieldInteractionCount,
        interactedFields: interactedFieldNames,
      } = summarizeInteractedFields(interactedFields.current);

      sendAnalytic(
        analyticsEvents.formFieldInteraction(
          detectedFormType,
          'summary',
          isEdit ? 'edit_abandonment' : 'add_abandonment',
        ),
        {
          formTitle: title,
          fieldInteractionCount: finalFieldInteractionCount,
          interactedFields: interactedFieldNames,
          formDuration: formDuration / 1000,
          previousPage: document.referrer || 'direct',
          timestamp: new Date().toISOString(),
        },
      );
    }

    // Modal close tracking removed - form abandonment is sufficient

    // Reset form tracking for next session
    formStartTime.current = null;
    setFieldInteractionCount(0);
    previousStateRef.current = null;
    isFirstRender.current = true;
    interactedFields.current.clear();
    setFilledFieldsCount(0);

    handleClose();
  };
  return (
    <>
      <Dialog
        onClose={handleFormClose}
        open={open}
        maxWidth={maxWidth}
        fullWidth
      >
        <DialogTitle
          onClose={handleFormClose}
          testIds={{
            dialogCloseButton: testId
              ? `${testId}-close-button`
              : 'dialog-close-button',
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <Box display='flex' alignItems='center' gap={1}>
              {title}
              {titleAdornment}
            </Box>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='right'
            >
              {copyId && (
                <Box display='flex' justifyContent='end' alignItems='center'>
                  <CopyId id={copyId} />
                </Box>
              )}
              {menuItems && (
                <IconButton
                  testId={
                    testId ? `${testId}-menu-button` : 'dialog-menu-button'
                  }
                  onClick={(event: React.MouseEvent<HTMLElement>) => {
                    openContextMenu(event.currentTarget);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <Form onSubmit={handleFormSubmit}>
          <DialogContent>{children}</DialogContent>
          <DialogFooter>
            <Box display='flex' justifyContent='end'>
              <Button
                label={formButton}
                dataTestId={
                  testId ? `${testId}-submit-button` : 'dialog-submit-button'
                }
                type='submit'
                disabled={loading || disabled}
              />
            </Box>
          </DialogFooter>
        </Form>
      </Dialog>
      {menuItems && (
        <Menu
          anchorEl={contextMenuAnchorEl}
          open={contextMenuOpen}
          onClose={closeContextMenu}
          onClick={closeContextMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ cursor: 'pointer' }}
        >
          {menuItems}
        </Menu>
      )}
      <ConfirmationModal
        open={exitConfirmationOpen}
        title={t('shared:wizardTitle', { wizardTitle: title })}
        bodyText={t('shared:wizardInterruptionMsg')}
        onConfirm={() => {
          setExitConfirmationOpen(false);
          handleFormClose();
        }}
        onCancel={() => setExitConfirmationOpen(false)}
      />
    </>
  );
};

export default FormModal;
